import { isGlobalFeatureOn, getCachedGlobalFeatureFlagsForSync } from '../../shell/feature-flags/globalFeatureFlagsService';
import { buildExpenseListSql, matchesExpenseListQuery, SQLITE_EXPENSE_IS_OVERDUE, todayIsoDate } from './expenseListSql';
import type { ExpenseListQuery, ExpenseListSummary } from '../domain/expenseTypes';
import { EXPENSES_LIST_EXPORT_MAX_ROWS } from '../domain/expenseTypes';
import {
  DEFAULT_EXPENSE_CATEGORY_SLUGS,
  EXPENSE_CATEGORY_LABELS,
  type ExpenseCategorySlug,
} from '../domain/expenseCategories';
import type {
  ExpenseCategoryRow,
  ExpenseRow,
  ExpenseSettlementRow,
  ExpensesSummary,
  LinkedPurchaseExpenseRow,
  ProfitLossSummary,
} from '../domain/expenseTypes';
import { invalidateReportsCache, reportsCacheKey, withReportsCache } from '../../reports/infra/reportsRemoteCache';
import { getWebExpensesSnapshotCache } from './webExpensesSnapshotCache';
import { invokePendingFiscalSideEffects } from '../../sync/core/manualSync';
import { supabase } from '../../shared/supabase/supabaseClient';

function expenseRowsFromWebCache(): ExpenseRow[] {
  const cached = getWebExpensesSnapshotCache();
  if (!cached) return [];
  return cached.expenses.map((row) => mapExpenseRow(row as Record<string, unknown>));
}

// matchesExpenseListQuery vive en expenseListSql (módulo puro, testeable).
export { matchesExpenseListQuery };

function sortExpenseRows(rows: ExpenseRow[], sortBy: ExpenseListQuery['sortBy']): ExpenseRow[] {
  return [...rows].sort((a, b) => {
    switch (sortBy) {
      case 'amount_desc':
        return b.amount - a.amount || b.expenseDate.localeCompare(a.expenseDate);
      case 'amount_asc':
        return a.amount - b.amount || b.expenseDate.localeCompare(a.expenseDate);
      case 'created_desc':
        return b.createdAt.localeCompare(a.createdAt);
      case 'due_date_asc': {
        if (!a.dueDate && !b.dueDate) return b.expenseDate.localeCompare(a.expenseDate);
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate) || b.expenseDate.localeCompare(a.expenseDate);
      }
      case 'due_date_desc':
        return (b.dueDate ?? '').localeCompare(a.dueDate ?? '') || b.expenseDate.localeCompare(a.expenseDate);
      case 'expense_date_desc':
      default:
        return b.expenseDate.localeCompare(a.expenseDate) || b.createdAt.localeCompare(a.createdAt);
    }
  });
}

export async function requireExpensesModule(): Promise<void> {
  if (!(await isExpensesModuleEnabled())) {
    throw new Error('Gastos no está habilitado.');
  }
}

export async function flushExpenseSyncSafely(
  context: string
): Promise<{ ok: true } | { ok: false; error: unknown }> {
  try {
    await invokePendingFiscalSideEffects();
    return { ok: true };
  } catch (error) {
    console.warn(`[${context}] sync`, error);
    return { ok: false, error };
  }
}

async function fetchPosReportsRpc<T>(
  rpcName: string,
  branchId: string,
  fromIso: string,
  toIso: string,
  map: (payload: Record<string, unknown>) => T | null
): Promise<T | null> {
  const { data, error } = await supabase.schema('pos').rpc(rpcName, {
    p_branch_id: branchId,
    p_from: fromIso,
    p_to: toIso,
  });
  if (error || !data) return null;
  return map(data as Record<string, unknown>);
}

export function mapExpenseRow(row: Record<string, unknown>): ExpenseRow {
  return {
    id: String(row.id),
    branchId: String(row.branch_id),
    categoryId: String(row.category_id),
    categorySlug: row.category_slug != null ? String(row.category_slug) : undefined,
    categoryName: row.category_name != null ? String(row.category_name) : undefined,
    supplierId: row.supplier_id != null ? String(row.supplier_id) : null,
    supplierName: row.supplier_name != null ? String(row.supplier_name) : null,
    amount: Number(row.amount ?? 0),
    amountPaid: Number(row.amount_paid ?? 0),
    expenseDate: String(row.expense_date ?? ''),
    description: row.description != null ? String(row.description) : null,
    receiptRef: row.receipt_ref != null ? String(row.receipt_ref) : null,
    paymentStatus: row.payment_status === 'paid' || row.payment_status === 'partial' ? row.payment_status : 'unpaid',
    status: row.status === 'voided' ? 'voided' : 'active',
    purchaseOrderId: row.purchase_order_id != null ? String(row.purchase_order_id) : null,
    stockReceptionId: row.stock_reception_id != null ? String(row.stock_reception_id) : null,
    createdAt: String(row.created_at ?? ''),
    createdBy: row.created_by != null ? String(row.created_by) : null,
    voidedAt: row.voided_at != null ? String(row.voided_at) : null,
    voidedBy: row.voided_by != null ? String(row.voided_by) : null,
    voidReason: row.void_reason != null ? String(row.void_reason) : null,
    dueDate: row.due_date != null ? String(row.due_date) : null,
    // Bloque fiscal del comprobante recibido (Libro IVA compras).
    invoiceDocType: row.invoice_doc_type != null ? Number(row.invoice_doc_type) : null,
    invoicePv: row.invoice_pv != null ? Number(row.invoice_pv) : null,
    invoiceNumber: row.invoice_number != null ? Number(row.invoice_number) : null,
    supplierTaxId: row.supplier_tax_id != null ? String(row.supplier_tax_id) : null,
    invoiceSupplierName:
      row.invoice_supplier_name != null ? String(row.invoice_supplier_name) : null,
    invoiceIvaBreakdown:
      row.invoice_iva_breakdown != null && typeof row.invoice_iva_breakdown === 'object'
        ? (row.invoice_iva_breakdown as ExpenseRow['invoiceIvaBreakdown'])
        : null,
    invoicePerceptions:
      row.invoice_perceptions != null && typeof row.invoice_perceptions === 'object'
        ? (row.invoice_perceptions as ExpenseRow['invoicePerceptions'])
        : null,
  };
}

export async function isExpensesModuleEnabled(): Promise<boolean> {
  const flags = getCachedGlobalFeatureFlagsForSync();
  return isGlobalFeatureOn(flags, 'expenses_module');
}

export async function ensureLocalExpenseCategories(organizationId: string): Promise<void> {
    return;
}

export async function getCategoryIdBySlug(
  organizationId: string,
  slug: ExpenseCategorySlug
): Promise<string> {
    return `${organizationId}:${slug}`;
}

export async function listExpenseCategories(organizationId: string): Promise<ExpenseCategoryRow[]> {
    return DEFAULT_EXPENSE_CATEGORY_SLUGS.map((slug, i) => ({
          id: `${organizationId}:${slug}`,
          organizationId,
          slug,
          name: EXPENSE_CATEGORY_LABELS[slug],
          active: true,
          sortOrder: (i + 1) * 10,
        }));
}

export async function listExpensesForBranch(
  params: ExpenseListQuery & { limit?: number; offset?: number }
): Promise<ExpenseRow[]> {
    const { limit = 100, offset = 0, ...query } = params;
        const filtered = sortExpenseRows(
          expenseRowsFromWebCache().filter((row) => matchesExpenseListQuery(row, query)),
          query.sortBy
        );
        return filtered.slice(offset, offset + limit);
}

export async function countExpensesForBranch(query: ExpenseListQuery): Promise<number> {
    return expenseRowsFromWebCache().filter((row) => matchesExpenseListQuery(row, query)).length;
}

export async function summarizeExpensesForBranch(query: ExpenseListQuery): Promise<ExpenseListSummary> {
    const today = todayIsoDate();
        const rows = expenseRowsFromWebCache().filter((row) => matchesExpenseListQuery(row, query));
        let total = 0;
        let unpaidTotal = 0;
        let overdueCount = 0;
        let overdueTotal = 0;
        for (const row of rows) {
          total += row.amount;
          const unpaid = Math.max(0, row.amount - row.amountPaid);
          // Anuladas no suman a "falta pagar" ni a vencidas.
          if (row.status === 'active' && row.paymentStatus !== 'paid') unpaidTotal += unpaid;
          if (row.status === 'active' && row.dueDate && row.dueDate < today && row.paymentStatus !== 'paid') {
            overdueCount += 1;
            overdueTotal += unpaid;
          }
        }
        return { count: rows.length, total, unpaidTotal, overdueCount, overdueTotal };
}

/**
 * Llama al RPC server-side `pos.list_expenses_for_export` para obtener el set
 * completo de gastos filtrados, sin depender del cache RAM (que tiene LIMIT 5000).
 * Issue #751: el cache de pull_branch_expenses_since solo tiene los 5000 más
 * recientes, por lo que exportaciones de rangos antiguos eran incompletas.
 */
export async function fetchRemoteExpensesForExport(
  query: ExpenseListQuery,
  maxRows: number
): Promise<{ rows: ExpenseRow[]; total: number; truncated: boolean } | null> {
  // El RPC `list_expenses_for_export` hoy solo lista gastos activos
  // (e.status = 'active' fijo). Con el filtro "Anuladas" devolvería 0 filas,
  // así que acá se cae directo al cache local, que sí entiende 'voided'.
  // TODO: extender el RPC server-side para exportar anuladas.
  if (query.paymentStatus === 'voided') return null;
  try {
    const { data, error } = await supabase.schema('pos').rpc('list_expenses_for_export', {
      p_branch_id: query.branchId,
      p_from_date: query.fromDate || null,
      p_to_date: query.toDate || null,
      p_payment_status: query.paymentStatus ?? 'all',
      p_due_status: query.dueStatus ?? 'all',
      p_category_id: query.categoryId ?? null,
      p_supplier_id: query.supplierId ?? null,
      p_search: query.search ?? null,
      p_max_rows: maxRows,
    });
    if (error || !data) return null;
    const payload = data as Record<string, unknown>;
    const rawRows = Array.isArray(payload.rows) ? (payload.rows as Record<string, unknown>[]) : [];
    const rows = rawRows.map(mapExpenseRow);
    return {
      rows,
      total: Number(payload.total ?? 0),
      truncated: Boolean(payload.truncated),
    };
  } catch (err) {
    console.warn('[listExpensesForExport] remote RPC failed, falling back to cache', err);
    return null;
  }
}

export async function listExpensesForExport(
  query: ExpenseListQuery,
  maxRows = EXPENSES_LIST_EXPORT_MAX_ROWS
): Promise<{ rows: ExpenseRow[]; truncated: boolean }> {
  // Intentar primero el RPC server-side (no tiene el límite de 5000 del cache)
  const remote = await fetchRemoteExpensesForExport(query, maxRows);
  if (remote) {
    return { rows: remote.rows, truncated: remote.truncated };
  }

  // Fallback: leer del cache RAM (puede estar incompleto si >5000 gastos)
  console.warn('[listExpensesForExport] using cache fallback — export may be incomplete for branches with >5000 expenses');
  const total = await countExpensesForBranch(query);
  const rows = await listExpensesForBranch({ ...query, limit: maxRows, offset: 0 });
  return { rows, truncated: total > maxRows };
}

/**
 * Busca una factura en el snapshot cache. Si no está y se conoce la sucursal,
 * fuerza un pull remoto y reintenta — cubre deep-links y acciones sobre
 * facturas que todavía no entraron al cache (o entraron filtradas).
 */
export async function getExpenseById(
  expenseId: string,
  opts?: { branchId?: string }
): Promise<ExpenseRow | null> {
    const hit = expenseRowsFromWebCache().find((row) => row.id === expenseId);
        if (hit || !opts?.branchId) return hit ?? null;
        try {
          const { pullExpensesWhenNeeded } = await import('../../sync/core/pullExpensesWhenNeeded');
          await pullExpensesWhenNeeded(opts.branchId, { force: true, requireFresh: true });
        } catch (error) {
          console.warn('[getExpenseById] remote fallback pull failed', error);
        }
        return expenseRowsFromWebCache().find((row) => row.id === expenseId) ?? null;
}

export async function getExpenseByPurchaseOrderId(purchaseOrderId: string): Promise<ExpenseRow | null> {
    return (await listExpensesByPurchaseOrderId(purchaseOrderId))[0] ?? null;
}

/**
 * Todos los gastos activos ligados a una OC (uno por recepción que generó gasto),
 * más reciente primero. Una compra directa puede tener varios.
 */
export async function listExpensesByPurchaseOrderId(purchaseOrderId: string): Promise<ExpenseRow[]> {
    return expenseRowsFromWebCache()
          .filter((row) => row.purchaseOrderId === purchaseOrderId && row.status === 'active')
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Un SELECT para las colas de Compras. Una fila por gasto ligado a OC. */
export async function listLinkedPurchaseExpensesForBranch(
  branchId: string,
): Promise<LinkedPurchaseExpenseRow[]> {
    return expenseRowsFromWebCache()
          .filter((r) => r.branchId === branchId && r.status === 'active' && r.purchaseOrderId)
          .map((row) => ({
            expenseId: row.id,
            purchaseOrderId: row.purchaseOrderId!,
            paymentStatus: row.paymentStatus,
            dueDate: row.dueDate,
            amount: row.amount,
            amountPaid: row.amountPaid,
            invoiceDocType: row.invoiceDocType ?? null,
          }));
}

export function mapSettlementRow(r: Record<string, unknown>): ExpenseSettlementRow {
  return {
    id: String(r.id),
    expenseId: String(r.expense_id),
    method: String(r.method) as ExpenseSettlementRow['method'],
    amount: Number(r.amount ?? 0),
    paidAt: String(r.paid_at ?? ''),
    cashOutflowId: r.cash_outflow_id != null ? String(r.cash_outflow_id) : null,
    status: r.status === 'voided' ? 'voided' : 'active',
    note: r.note != null ? String(r.note) : null,
    createdBy: r.created_by != null ? String(r.created_by) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
  };
}

export async function listSettlementsForExpense(
  expenseId: string,
  opts?: { branchId?: string }
): Promise<ExpenseSettlementRow[]> {
    const cached = getWebExpensesSnapshotCache();
        if (!cached) return [];
        return cached.settlements
          .filter((s) => {
            if (s.expenseId !== expenseId) return false;
            // Scoping: si conocemos la sucursal de la factura padre y no
            // coincide, el pago no es de esta sucursal — no se muestra.
            if (opts?.branchId && s.expenseBranchId && s.expenseBranchId !== opts.branchId) {
              return false;
            }
            return true;
          })
          .sort((a, b) => (a.paidAt < b.paidAt ? 1 : a.paidAt > b.paidAt ? -1 : 0))
          .map((s) => ({
            id: s.settlementId,
            expenseId: s.expenseId,
            method: s.method,
            amount: s.settlementAmount,
            paidAt: s.paidAt,
            cashOutflowId: s.cashOutflowId,
            status: s.settlementStatus,
            note: s.note,
            createdBy: s.createdBy,
            voidedAt: s.voidedAt,
            voidedBy: s.voidedBy,
            voidReason: s.voidReason,
          }));
}

export async function sumExpensesForBranchPeriod(params: {
  branchId: string;
  fromDate: string;
  toDate: string;
}): Promise<number> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  // fromDate/toDate vacíos = preset "Todos" (sin cota de fecha).
  const from = params.fromDate || '';
  const to = params.toDate || '';
  return expenseRowsFromWebCache()
    .filter(
      (row) =>
        row.branchId === params.branchId &&
        row.status === 'active' &&
        row.purchaseOrderId === null &&
        (!from || row.expenseDate >= from) &&
        (!to || row.expenseDate <= to)
    )
    .reduce((sum, row) => sum + row.amount, 0);
}

export function invalidateRemoteExpensesReportsCache(branchId: string): void {
  invalidateReportsCache(`reports_branch_expenses_summary|${branchId}|`);
  invalidateReportsCache(`reports_branch_profit_loss_summary|${branchId}|`);
}

export async function fetchRemoteExpensesSummary(
  branchId: string,
  fromIso: string,
  toIso: string
): Promise<ExpensesSummary | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_expenses_summary', branchId, fromIso, toIso]),
    () => fetchPosReportsRpc('reports_branch_expenses_summary', branchId, fromIso, toIso, (j) => {
      const byCategory = Array.isArray(j.by_category)
        ? (j.by_category as Array<{ category: string; total: number }>)
        : [];
      const bySettlementMethod = Array.isArray(j.by_settlement_method)
        ? (j.by_settlement_method as Array<{ method: ExpenseSettlementRow['method']; total: number }>)
        : [];
      const byPaymentStatus = Array.isArray(j.by_payment_status)
        ? (j.by_payment_status as Array<{ status: ExpenseRow['paymentStatus']; total: number }>)
        : [];
      return {
        expensesTotal: Number(j.expenses_total ?? 0),
        cashOutflowsTotal: Number(j.cash_outflows_total ?? 0),
        unpaidTotal: Number(j.unpaid_total ?? 0),
        byCategory,
        bySettlementMethod,
        byPaymentStatus,
        linkedToCashSessionTotal: Number(j.linked_to_cash_session_total ?? 0),
        unlinkedToCashSessionTotal: Number(j.unlinked_to_cash_session_total ?? 0),
      };
    }),
    undefined,
    { category: 'reports', name: 'reports_branch_expenses_summary' }
  );
}

export async function fetchRemoteProfitLossSummary(
  branchId: string,
  fromIso: string,
  toIso: string
): Promise<ProfitLossSummary | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_profit_loss_summary', branchId, fromIso, toIso]),
    () => fetchPosReportsRpc('reports_branch_profit_loss_summary', branchId, fromIso, toIso, (j) => ({
      salesTotal: Number(j.sales_total ?? 0),
      expensesTotal: Number(j.expenses_total ?? 0),
      grossMargin: Number(j.gross_margin ?? 0),
    })),
    undefined,
    { category: 'reports', name: 'reports_branch_profit_loss_summary' }
  );
}
